/**
 * Global theme utilities
 * Load this file before other scripts in theme.liquid
 */
window.theme = window.theme || {};

// Merge with existing theme.utils (may have formatMoney from js-theme.liquid)
theme.utils = Object.assign(theme.utils || {}, {
  /**
   * Debounce function - delays execution until after wait milliseconds
   * have elapsed since the last time the debounced function was invoked
   * @param {Function} fn - Function to debounce
   * @param {number} wait - Milliseconds to delay
   * @returns {Function} Debounced function
   */
  debounce: (fn, wait = 300) => {
    let timeoutId = null;

    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(...args), wait);
    };
  },
  fetchConfig: (type = 'json') => {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  },
  domParser: new DOMParser(),

  togglePanel(trigger, panel) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    if (isExpanded) {
      this.closePanel(trigger, panel);
    } else {
      this.openPanel(trigger, panel);
    }
    return !isExpanded;
  },

  openPanel(trigger, panel) {
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (panel) panel.setAttribute('aria-hidden', 'false');
  },

  closePanel(trigger, panel) {
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) panel.setAttribute('aria-hidden', 'true');
  },

  initDragScroll(container) {
    let isDragging = false;
    let startX = 0;
    let scrollLeft = 0;
    let moved = false;

    function hasOverflow() {
      return container.scrollWidth > container.clientWidth;
    }

    function onPointerDown(e) {
      if (!hasOverflow()) return;
      isDragging = true;
      moved = false;
      startX = e.pageX;
      scrollLeft = container.scrollLeft;
      container.addEventListener('pointermove', onPointerMove);
      container.addEventListener('pointerup', onPointerUp);
      container.addEventListener('pointercancel', onPointerUp);
    }

    function onPointerMove(e) {
      if (!isDragging) return;
      const walk = e.pageX - startX;
      if (Math.abs(walk) > 5) {
        moved = true;
        container.classList.add('is-dragging');
      }
      container.scrollLeft = scrollLeft - walk;
    }

    function onPointerUp() {
      isDragging = false;
      container.classList.remove('is-dragging');
      container.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('pointerup', onPointerUp);
      container.removeEventListener('pointercancel', onPointerUp);
    }

    container.addEventListener('pointerdown', onPointerDown);

    return {
      destroy() {
        container.removeEventListener('pointerdown', onPointerDown);
        container.removeEventListener('pointermove', onPointerMove);
        container.removeEventListener('pointerup', onPointerUp);
        container.removeEventListener('pointercancel', onPointerUp);
      },
      get hasMoved() {
        return moved;
      }
    };
  }
});

/**
 * Pub/Sub event system
 * Provides a simple publish/subscribe pattern for component communication
 */
theme.pubsub = (() => {
  const subscribers = new Map();

  return {
    events: Object.freeze({
      CART_UPDATE: 'cart-update',
      CART_ADD: 'cart:add',
      CART_REMOVE: 'cart:remove',
      CART_CLEAR: 'cart:clear',
      CART_DRAWER_OPEN: 'cart-drawer:open',
      CART_DRAWER_CLOSE: 'cart-drawer:close',
      QUANTITY_UPDATE: 'quantity-update',
      VARIANT_CHANGE: 'variant-change',
      OPTION_VALUE_CHANGE: 'option-value-selection-change',
      CART_ERROR: 'cart-error',
    }),

    subscribe(eventName, callback) {
      if (!subscribers.has(eventName)) {
        subscribers.set(eventName, new Set());
      }
      subscribers.get(eventName).add(callback);

      return () => subscribers.get(eventName)?.delete(callback);
    },

    publish(eventName, data) {
      subscribers.get(eventName)?.forEach((cb) => cb(data));
    },

    clear(eventName) {
      eventName ? subscribers.delete(eventName) : subscribers.clear();
    },
  };
})();

if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    constructor() {
      super();
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = theme.utils.debounce(this.updateAnnouncementHeight.bind(this), 100);
    }

    connectedCallback() {
      this.hideOnScroll = this.dataset.hideOnScroll === 'true';
      this.isTransparent = this.classList.contains('header--transparent');
      this.headerSection = this.closest('.header-section') || this.parentElement;

      // The height is needed whenever the announcement bar is sticky, not just on
      // transparent-header pages: the header offsets itself below the bar, and a bar
      // measured at 0 puts both of them at top:0, overlapping.
      this.initHeightTracking();

      if (!this.hideOnScroll && !this.isTransparent) return;
      this.lastScrollY = window.scrollY;
      this.threshold = 5;
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);
    }

    initHeightTracking() {
      this.updateAnnouncementHeight();
      window.addEventListener('resize', this.handleResize, { passive: true });
      window.addEventListener('load', () => this.updateAnnouncementHeight(), { once: true });

      const bar = document.querySelector('announcement-bar');
      if (bar && 'ResizeObserver' in window) {
        const ro = new ResizeObserver(() => this.updateAnnouncementHeight());
        ro.observe(bar);
      }
    }

    updateAnnouncementHeight() {
      const bar = document.querySelector('announcement-bar');
      const height = bar ? bar.offsetHeight : 0;
      if (this.headerSection) {
        this.headerSection.style.setProperty('--announcement-bar-height', height + 'px');
      }
      // Also on :root, so a rule on any element can offset itself below the bar.
      document.documentElement.style.setProperty('--announcement-bar-height', height + 'px');

      // --header-nav-height means "the header is floating OVER the content, clear it" —
      // collection-banner, page-banner, article-banner and the wishlist header all add it
      // to their top padding. It must stay unset when the header sits in the flow, or a
      // plain PLP banner pays for a header it is already below: measured 88px of padding
      // on mobile (68 + 20) where 20 is correct. Publishing it unconditionally, as this
      // method briefly did, is what caused that.
      if (this.isTransparent) {
        document.documentElement.style.setProperty('--header-nav-height', this.offsetHeight + 'px');
      }

      this.announcementBarHeight = height;
    }

    handleScroll() {
      const currentY = window.scrollY;

      if (document.body.style.overflow === 'hidden') {
        this.lastScrollY = currentY;
        return;
      }

      if (this.isTransparent) {
        if (currentY >= (this.announcementBarHeight || 1)) {
          this.classList.add('header--scrolled');
        } else {
          this.classList.remove('header--scrolled');
        }
      }

      if (this.hideOnScroll) {
        const diff = currentY - this.lastScrollY;
        if (diff > this.threshold && currentY > 200) {
          this.classList.add('header--hidden');
        } else if (diff < -this.threshold || currentY <= 200) {
          this.classList.remove('header--hidden');
        }
        this.lastScrollY = currentY;
      }
    }
  }
  customElements.define('sticky-header', StickyHeader);
}

document.addEventListener('DOMContentLoaded', () => {
  const cartTrigger = document.querySelector('[data-cart-drawer-trigger]');

  if (cartTrigger && window.theme?.pubsub) {
    cartTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      theme.pubsub.publish(theme.pubsub.events.CART_DRAWER_OPEN);
    });
  }
});

if (!customElements.get('responsive-accordion')) {
  let responsiveAccordionCounter = 0;

  class ResponsiveAccordion extends HTMLElement {
    constructor() {
      super();
      this.breakpoint = parseInt(this.dataset.breakpoint) || 750;
      this.mediaQuery = window.matchMedia(`(max-width: ${this.breakpoint - 1}px)`);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.accordionToggle = null;
      this.originalContent = null;
      this.headingElement = null;
    }

    connectedCallback() {
      this.mediaQuery.addEventListener('change', this.handleMediaChange);
      this.handleMediaChange(this.mediaQuery);
    }

    disconnectedCallback() {
      this.mediaQuery.removeEventListener('change', this.handleMediaChange);
    }

    handleMediaChange(event) {
      if (event.matches) {
        this.setupMobileAccordion();
      } else {
        this.removeMobileAccordion();
      }
    }

    setupMobileAccordion() {
      if (this.accordionToggle) return;

      this.headingElement = this.querySelector('.h1, .h2, .h3, .h4, .h5, h1, h2, h3, h4, h5, [class*="__heading"]');
      const triggerText = this.dataset.triggerText || this.headingElement?.textContent.trim() || '';
      const contentId = `responsive-accordion-${++responsiveAccordionCounter}`;

      this.accordionToggle = document.createElement('accordion-toggle');
      this.accordionToggle.className = 'responsive-accordion__toggle';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'responsive-accordion__trigger';
      button.setAttribute('aria-expanded', 'false');
      button.setAttribute('aria-controls', contentId);
      button.setAttribute('data-accordion-trigger', '');

      const label = document.createElement('span');
      label.className = 'responsive-accordion__label';
      label.textContent = triggerText;

      const icon = document.createElement('span');
      icon.className = 'responsive-accordion__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = '<svg class="icon icon-chevron-down" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M5 7.5l5 5 5-5"/></svg>';

      button.appendChild(label);
      button.appendChild(icon);

      const contentWrapper = document.createElement('div');
      contentWrapper.className = 'responsive-accordion__content';
      contentWrapper.id = contentId;
      contentWrapper.setAttribute('data-accordion-content', '');

      const contentInner = document.createElement('div');
      contentInner.className = 'accordion-toggle__content-inner';

      if (this.headingElement) {
        this.headingElement.style.display = 'none';
      }

      this.originalContent = Array.from(this.children);
      this.originalContent.forEach(child => {
        if (child !== this.headingElement) {
          contentInner.appendChild(child);
        }
      });

      contentWrapper.appendChild(contentInner);
      this.accordionToggle.appendChild(button);
      this.accordionToggle.appendChild(contentWrapper);
      this.appendChild(this.accordionToggle);
    }

    removeMobileAccordion() {
      if (!this.accordionToggle) return;

      const contentInner = this.accordionToggle.querySelector('.accordion-toggle__content-inner');
      if (contentInner) {
        Array.from(contentInner.children).forEach(child => this.appendChild(child));
      }

      this.accordionToggle.remove();
      this.accordionToggle = null;

      if (this.headingElement) {
        this.headingElement.style.display = '';
      }
    }
  }

  customElements.define('responsive-accordion', ResponsiveAccordion);
}
